import { API_CONFIG } from '../../../../config/api';
import { authService } from '../../../../services/authService';

export type StatusRegistro =
    | 'ATIVO'
    | 'RECUSADO'
    | 'CANCELADO'
    | 'LIQUIDADO'
    | 'Aceita'
    | 'REGISTRADO'
    | 'ERRO'
    | 'REJEITADO'
    | 'LIQUIDADO_PARCIALMENTE'
    | 'NAO_REGISTRADO';

export const STATUS_REGISTROS: StatusRegistro[] = ['ATIVO', 'RECUSADO', 'CANCELADO', 'LIQUIDADO', 'NAO_REGISTRADO'];

export const STATUS_NAO_LIQUIDAVEL: StatusRegistro[] = [
    'RECUSADO',
    'CANCELADO',
    'LIQUIDADO',
    'ERRO',
    'REJEITADO',
];

export const podeSerLiquidado = (status: StatusRegistro): boolean => {
    return status === 'ATIVO';
};

export const podeSerCancelado = (status: StatusRegistro): boolean => {
    return status === 'ATIVO';
};

export const mapDisplayStatusToApiStatus = (status: string): StatusRegistro[] => {
    switch (status) {
        case 'ATIVO':                return ['Aceita'];
        case 'RECUSADO':             return ['ERRO'];
        case 'CANCELADO':            return ['CANCELADO'];
        case 'LIQUIDADO':            return ['LIQUIDADO'];
        case 'NAO_REGISTRADO':       return ['NAO_REGISTRADO'];
        default:                     return [status as StatusRegistro];
    }
};

export const mapDisplayStatusToApiStatusAll = (status: string): StatusRegistro[] => {
    switch (status) {
        case 'ATIVO':                return ['Aceita', 'REGISTRADO', 'LIQUIDADO_PARCIALMENTE', 'ATIVO'];
        case 'RECUSADO':             return ['ERRO', 'REJEITADO', 'RECUSADO'];
        case 'CANCELADO':            return ['CANCELADO'];
        case 'LIQUIDADO':            return ['LIQUIDADO'];
        case 'NAO_REGISTRADO':       return ['NAO_REGISTRADO'];
        default:                     return [status as StatusRegistro];
    }
};

export interface CarteiraFundo {
    codigo_carteira: string;
    nome_fantasia_fundo: string;
}

export interface RegistrosQueryParams {
    numeroContrato?: string;
    carteiras?: string[];
    dataAquisicao?: string;
    status?: StatusRegistro[];
    page?: number;
    size?: number;
    sort?: string;
}

export interface RegistroDetalhe {
    id_parcela: number;
    seu_numero: string;
    numero_parcela: number;
    valor_nominal: number;
    saldo_registrado: number | null;
    status: StatusRegistro;
    data_vencimento: string;
    mensagem?: string;
}

export interface RegistroAquisicao {
    idContrato: number;
    carteira: string;
    cedente: string;
    cnpjCedente: string;
    cnpjOriginador: string;
    dtAquisicao: string;
    dataRegistro: string;
    fundo: string;
    idRegistradoraContrato: string;
    ipoc: string;
    numeroContrato: string;
    originador: string;
    numeroParcela: number | null;
    qtdParcelas: number;
    saldoRegistrado: number;
    status: StatusRegistro | null;
    statusCalculado?: string;
    registradora: string;
    tipoRecebivel: string;
    valorAtual: number;
    valorNominalTotal: number;
    mensagem?: string;
    detalhes?: RegistroDetalhe[];
}
export const computeDisplayStatus = (registro: RegistroAquisicao): StatusRegistro => {
    if (registro.detalhes && registro.detalhes.length > 0) {
        const todasLiquidadas = registro.detalhes.every(d => d.status === 'LIQUIDADO');
        if (todasLiquidadas) return 'LIQUIDADO';
    }

    const status = (registro.statusCalculado as StatusRegistro | null) ?? registro.status;

    switch (status) {
        case 'Aceita':
        case 'REGISTRADO':
        case 'LIQUIDADO_PARCIALMENTE':
            return 'ATIVO';
        case 'ERRO':
        case 'REJEITADO':
            return 'RECUSADO';
        default:
            return status as StatusRegistro;
    }
};

export interface RegistrosApiResponse {
    content: RegistroAquisicao[];
    totalElements: number;
    totalPages: number;
    pageSize: number;
    pageNumber: number;
    first: boolean;
    last: boolean;
}

export interface LiquidacaoRequest {
    tipoLiquidacao: 'CONTRATO';
    id_contratos: number[];
}

export interface CancelamentoRequest {
    id_ativos: string[];
    id_lote: string;
    razao_cancelamento: string;
}

export interface ParcelaLiquidacao {
    id_parcela: number;
    vl_pago: number;
    dtMovimento: string;
}

export interface LiquidacaoParcelasRequest {
    tipoLiquidacao: 'PARCELA';
    parcelas: ParcelaLiquidacao[];
}

export interface StatusContagem {
    status: string;
    quantidade: number;
}

export class RegistrosService {
    private baseUrl: string;
    private liquidacaoUrl: string;

    constructor() {
        this.baseUrl = `${API_CONFIG.BASE_URL_JAVA}/api/v1/registros`;
        this.liquidacaoUrl = `${API_CONFIG.BASE_URL_JAVA}/api/v1/liquidacao`;
    }

    async fetchRegistros(params: RegistrosQueryParams): Promise<RegistrosApiResponse | RegistroAquisicao[]> {
        const queryParams = new URLSearchParams();

        queryParams.append('numeroContrato', params.numeroContrato || '');
        if (params.carteiras && params.carteiras.length > 0) {
            params.carteiras.forEach(carteira => queryParams.append('carteiras', carteira));
        }
        if (params.dataAquisicao) queryParams.append('dataAquisicao', params.dataAquisicao);
        if (params.status && params.status.length > 0) {
            params.status.forEach(s => queryParams.append('status', s));
        }
        if (params.page !== undefined) queryParams.append('page', params.page.toString());
        if (params.size !== undefined) queryParams.append('size', params.size.toString());
        if (params.sort) queryParams.append('sort', params.sort);

        const url = `${this.baseUrl}?${queryParams.toString()}`;

        const token = await authService.getValidToken();

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Erro ao buscar registros: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    }

    async fetchParcelas(idContrato: number): Promise<RegistroDetalhe[]> {
        const url = `${this.baseUrl}/${idContrato}`;
        const token = await authService.getValidToken();

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Erro ao buscar parcelas: ${response.statusText}`);
        }

        return await response.json();
    }

    async fetchCarteirasFundos(): Promise<CarteiraFundo[]> {
        const url = `${this.baseUrl}/carteiras-fundos`;

        const token = await authService.getValidToken();

        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error(`Erro ao buscar carteiras de fundos: ${response.statusText}`);
        }

        const data = await response.json();
        return data;
    }

    async liquidarContratos(contratoIds: number[]): Promise<any> {
        const token = await authService.getValidToken();

        const body: LiquidacaoRequest = {
            tipoLiquidacao: 'CONTRATO',
            id_contratos: contratoIds
        };

        const response = await fetch(this.liquidacaoUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Erro ao liquidar contratos: ${response.statusText}`);
        }

        return await response.json();
    }

    async liquidarParcelas(parcelas: ParcelaLiquidacao[]): Promise<any> {
        const token = await authService.getValidToken();

        const body: LiquidacaoParcelasRequest = {
            tipoLiquidacao: 'PARCELA',
            parcelas: parcelas
        };

        const response = await fetch(this.liquidacaoUrl, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Erro ao liquidar parcelas: ${response.statusText}`);
        }

        return await response.json();
    }

    async fetchStatusContagem(dataCessao: string, carteiras?: string[]): Promise<StatusContagem[]> {
        const queryParams = new URLSearchParams();
        queryParams.append('dataCessao', dataCessao);
        if (carteiras && carteiras.length > 0) {
            carteiras.forEach(c => queryParams.append('carteiras', c));
        }
        const url = `${this.baseUrl}/status-contagem?${queryParams.toString()}`;
        const token = await authService.getValidToken();
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });
        if (!response.ok) {
            throw new Error(`Erro ao buscar contagem de status: ${response.statusText}`);
        }
        return await response.json();
    }

    async cancelarAtivos(idsAtivos: string[], idLote: string, razaoCancelamento: string): Promise<any> {
        const token = await authService.getValidToken();

        const body: CancelamentoRequest = {
            id_ativos: idsAtivos,
            id_lote: idLote,
            razao_cancelamento: razaoCancelamento
        };

        const response = await fetch(`${this.baseUrl}/cancelar/batch`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(body)
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || `Erro ao cancelar registros: ${response.statusText}`);
        }

        return await response.json();
    }
}

export const registrosService = new RegistrosService();
